<template>
  <div class="container">
    <div class="header">
      <h1>NFT Profit Calculator</h1>
      <button 
        @click="toggleTheme" 
        class="theme-toggle" 
        aria-label="Toggle dark mode"
      >
        <span class="theme-icon">{{ themeIcon }}</span>
      </button>
    </div>
    
    <div class="calculator">
      <!-- Wallet Address Input -->
      <div class="input-group">
        <label for="walletAddress">Wallet Address</label>
        <div class="wallet-input-container">
          <input 
            type="text" 
            id="walletAddress" 
            class="wallet-input" 
            placeholder="0x..."
            v-model="walletAddress"
          >
          <button 
            @click="viewNFTs" 
            class="view-nfts-btn"
            :disabled="!walletAddress.trim()"
          >
            View NFTs
          </button>
        </div>
      </div>

      <!-- Calculator Inputs -->
      <div class="input-group">
        <label for="buyPrice">Buy Price (ETH)</label>
        <input 
          type="number" 
          id="buyPrice" 
          step="0.000001" 
          placeholder="0.0"
          v-model="buyPrice"
          @input="calculateProfit"
        >
      </div>

      <div class="input-group">
        <label for="sellPrice">Sell Price (ETH)</label>
        <input 
          type="number" 
          id="sellPrice" 
          step="0.000001" 
          placeholder="0.0"
          v-model="sellPrice"
          @input="calculateProfit"
        >
      </div>

      <div class="input-group">
        <label for="marketplaceFee">Marketplace Fee (%)</label>
        <input 
          type="number" 
          id="marketplaceFee" 
          step="0.1" 
          placeholder="2.5"
          v-model="marketplaceFee"
          @input="calculateProfit"
        >
        <span class="calculation-breakdown" id="marketplaceFeeBreakdown">
          = {{ marketplaceFeeAmount.toFixed(6) }} ETH (${{ marketplaceFeeUsd.toFixed(2) }})
        </span>
      </div>

      <div class="input-group">
        <label for="collectionFee">Collection Fee (%)</label>
        <input 
          type="number" 
          id="collectionFee" 
          step="0.1" 
          placeholder="2.5"
          v-model="collectionFee"
          @input="calculateProfit"
        >
        <span class="calculation-breakdown" id="collectionFeeBreakdown">
          = {{ collectionFeeAmount.toFixed(6) }} ETH (${{ collectionFeeUsd.toFixed(2) }})
        </span>
      </div>

      <div class="input-group">
        <label for="ethUsdRate">ETH to USD Rate</label>
        <input 
          type="number" 
          id="ethUsdRate" 
          step="0.01" 
          v-model="ethUsdRate"
          @input="calculateProfit"
        >
        <div id="ethPriceTimestamp" class="timestamp">
          {{ ethPriceTimestamp }}
        </div>
      </div>
      
      <!-- Gas Price Information -->
      <div v-if="showGasPriceInfo" class="gas-price-info">
        <h3>Current Gas Prices (Gwei)</h3>
        <div class="gas-price-grid">
          <div class="gas-price-item">
            <span class="gas-price-label">Slow:</span>
            <span class="gas-price-value">{{ gasPrices.slow || '-' }}</span>
          </div>
          <div class="gas-price-item">
            <span class="gas-price-label">Standard:</span>
            <span class="gas-price-value">{{ gasPrices.standard || '-' }}</span>
          </div>
          <div class="gas-price-item">
            <span class="gas-price-label">Fast:</span>
            <span class="gas-price-value">{{ gasPrices.fast || '-' }}</span>
          </div>
        </div>
        <div class="gas-price-source">
          <small>Source: {{ gasPrices.source || '-' }}</small>
        </div>
      </div>

      <div class="button-group">
        <button @click="calculateProfit">Calculate Profit</button>
        <button @click="clearAll" class="secondary">Clear All</button>
      </div>

      <!-- Results -->
      <div class="result">
        <h2>Profit Details</h2>
        <div class="result-item">
          <span>Profit (ETH):</span>
          <span :class="profitClass">{{ profit.toFixed(6) }}</span>
        </div>
        <div class="result-item">
          <span>Profit (USD):</span>
          <span :class="profitClass">${{ profitUsd.toFixed(2) }}</span>
        </div>
        <div class="result-item">
          <span>Net Sell Price (ETH):</span>
          <span>{{ netSellPrice.toFixed(6) }}</span>
        </div>
        <div class="result-item">
          <span>Net Sell Price (USD):</span>
          <span>${{ netSellPriceUsd.toFixed(2) }}</span>
        </div>
        <div class="result-item">
          <span>ROI:</span>
          <span :class="roiClass">{{ roi.toFixed(2) }}%</span>
        </div>
        <div class="result-item">
          <span>Marketplace Fee (ETH):</span>
          <span>{{ marketplaceFeeAmount.toFixed(6) }}</span>
        </div>
        <div class="result-item">
          <span>Marketplace Fee (USD):</span>
          <span>${{ marketplaceFeeUsd.toFixed(2) }}</span>
        </div>
        <div class="result-item">
          <span>Collection Fee (ETH):</span>
          <span>{{ collectionFeeAmount.toFixed(6) }}</span>
        </div>
        <div class="result-item">
          <span>Collection Fee (USD):</span>
          <span>${{ collectionFeeUsd.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <div class="footer">
      <!-- Build timestamp will be injected here by the build process -->
    </div>

    <!-- NFT Grid Modal -->
    <NftGrid 
      v-model="showNftModal"
      :wallet-address="walletAddress"
      :debug-mode="debugMode"
      @close="handleNftModalClose"
      @nft-select="handleNftSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useCalculatorStore } from '@/stores/calculator'
import NftGrid from './components/NftGrid.vue'

// Store
const calculatorStore = useCalculatorStore()

// Check for debug mode in URL
const urlParams = new URLSearchParams(window.location.search)
const debugMode = urlParams.get('debug') === 'true'

// Reactive state
const walletAddress = ref('')
const buyPrice = ref('')
const sellPrice = ref('')
const marketplaceFee = ref('2.5')
const collectionFee = ref('2.5')
const ethUsdRate = ref('1800')

// Gas price state
const gasPrices = ref({
  slow: '-',
  standard: '-',
  fast: '-',
  source: 'Unknown'
})
const showGasPriceInfo = ref(false)

// ETH price state
const ethPriceTimestamp = ref('Updating...')

// Computed values
const buyPriceNum = computed(() => parseFloat(buyPrice.value) || 0)
const sellPriceNum = computed(() => parseFloat(sellPrice.value) || 0)
const marketplaceFeeNum = computed(() => parseFloat(marketplaceFee.value) || 0)
const collectionFeeNum = computed(() => parseFloat(collectionFee.value) || 0)
const ethUsdRateNum = computed(() => parseFloat(ethUsdRate.value) || 1800)

// Calculated values
const marketplaceFeeAmount = computed(() => (sellPriceNum.value * marketplaceFeeNum.value / 100))
const collectionFeeAmount = computed(() => (sellPriceNum.value * collectionFeeNum.value / 100))
const totalFee = computed(() => marketplaceFeeAmount.value + collectionFeeAmount.value)
const netSellPrice = computed(() => sellPriceNum.value - totalFee.value)
const profit = computed(() => netSellPrice.value - buyPriceNum.value)
const roi = computed(() => buyPriceNum.value !== 0 ? (profit.value / buyPriceNum.value) * 100 : 0)

// USD values
const profitUsd = computed(() => profit.value * ethUsdRateNum.value)
const netSellPriceUsd = computed(() => netSellPrice.value * ethUsdRateNum.value)
const marketplaceFeeUsd = computed(() => marketplaceFeeAmount.value * ethUsdRateNum.value)
const collectionFeeUsd = computed(() => collectionFeeAmount.value * ethUsdRateNum.value)

// CSS classes for styling
const profitClass = computed(() => {
  if (profit.value > 0) return 'profit-positive'
  if (profit.value < 0) return 'profit-negative'
  return 'profit-neutral'
})

const roiClass = computed(() => {
  if (roi.value > 0) return 'profit-positive'
  if (roi.value < 0) return 'profit-negative'
  return 'profit-neutral'
})

// Theme management
const getSystemTheme = (): 'dark' | 'light' => {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const currentTheme = ref(localStorage.getItem('theme') as 'dark' | 'light' || getSystemTheme())
const themeIcon = computed(() => currentTheme.value === 'dark' ? '☀️' : '🌙')

// Watch for system theme changes
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
const handleSystemThemeChange = (e: MediaQueryListEvent) => {
  if (!localStorage.getItem('theme')) {
    currentTheme.value = e.matches ? 'dark' : 'light'
    applyTheme(currentTheme.value)
  }
}
mediaQuery.addEventListener('change', handleSystemThemeChange)
  
// Cleanup on component unmount
onUnmounted(() => {
  mediaQuery.removeEventListener('change', handleSystemThemeChange)
})

// Apply theme to document root
const applyTheme = (theme: 'dark' | 'light') => {
  document.documentElement.setAttribute('data-theme', theme)
  // Ensure the theme is applied to the entire document
  const root = document.documentElement
  root.style.setProperty('color-scheme', theme)
  root.classList.toggle('dark', theme === 'dark')
  root.classList.toggle('light', theme === 'light')
}

const toggleTheme = () => {
  currentTheme.value = currentTheme.value === 'dark' ? 'light' : 'dark'
  localStorage.setItem('theme', currentTheme.value)
  applyTheme(currentTheme.value)
}

// Methods
const calculateProfit = () => {
  // Validation
  if (isNaN(buyPriceNum.value) || isNaN(sellPriceNum.value)) {
    alert('Please enter valid ETH values')
    return
  }
  
  // Save to store
  calculatorStore.updateValues({
    buyPrice: buyPrice.value,
    sellPrice: sellPrice.value,
    marketplaceFee: marketplaceFee.value,
    collectionFee: collectionFee.value,
    ethUsdRate: ethUsdRate.value,
    walletAddress: walletAddress.value
  })
}

const clearAll = () => {
  buyPrice.value = ''
  sellPrice.value = ''
  marketplaceFee.value = '2.5'
  collectionFee.value = '2.5'
  walletAddress.value = ''
  calculatorStore.clearValues()
}

const showNftModal = ref(false)

const viewNFTs = () => {
  console.log('[App] View NFTs clicked, wallet address:', walletAddress.value)
  if (walletAddress.value.trim()) {
    showNftModal.value = true
    console.log('[App] showNftModal set to:', showNftModal.value)
  } else {
    alert('Please enter a wallet address first')
  }
}

const handleNftModalClose = () => {
  showNftModal.value = false
}

const handleNftSelect = (nft: any) => {
  console.log('NFT selected:', nft)
  // You can add logic here to use the selected NFT's data
  // Don't close the modal here - let the NftModalWrapper handle the detail view
  // The modal will be closed when the user confirms the NFT selection
}

// API functions
const fetchGasPrices = async () => {
  try {
    const response = await fetch('/api/gas-prices')
    if (!response.ok) {
      throw new Error('Failed to fetch gas prices')
    }
    const data = await response.json()
    
    gasPrices.value = {
      slow: data.slow || '-',
      standard: data.standard || '-',
      fast: data.fast || '-',
      source: data.source || 'Unknown'
    }
    
    showGasPriceInfo.value = true
  } catch (error) {
    console.warn('Gas price service unavailable:', error)
    showGasPriceInfo.value = false
  }
}

const fetchEthPrice = async () => {
  try {
    const response = await fetch('/api/eth-price')
    if (!response.ok) {
      throw new Error('Failed to fetch ETH price from backend')
    }
    const data = await response.json()
    
    ethUsdRate.value = data.usd.toFixed(2)
    ethPriceTimestamp.value = `Last updated: ${new Date(data.timestamp).toLocaleTimeString()} (${data.source})`
    
    calculateProfit()
  } catch (error) {
    console.error('Error fetching ETH price:', error)
  }
}

// Initialize app
const initApp = async () => {
  console.log('Initializing app...')
  try {
    await Promise.allSettled([
      fetchGasPrices(),
      fetchEthPrice()
    ])
  } catch (error) {
    console.error('Unexpected error in initApp:', error)
  }
}

// Load saved values
const loadSavedValues = () => {
  const savedValues = calculatorStore.getSavedValues()
  if (savedValues) {
    buyPrice.value = savedValues.buyPrice || ''
    sellPrice.value = savedValues.sellPrice || ''
    marketplaceFee.value = savedValues.marketplaceFee || '2.5'
    collectionFee.value = savedValues.collectionFee || '2.5'
    ethUsdRate.value = savedValues.ethUsdRate || '1800'
    walletAddress.value = savedValues.walletAddress || ''
  }
}

// Lifecycle
onMounted(() => {
  // Apply theme
  applyTheme(currentTheme.value)
  
  // Load saved values
  loadSavedValues()
  
  // Initialize app
  initApp()
  
  // Set up periodic updates
  setInterval(() => {
    fetchGasPrices()
    fetchEthPrice()
  }, 5 * 60 * 1000)
})

// Watch for changes and save
watch([buyPrice, sellPrice, marketplaceFee, collectionFee, ethUsdRate, walletAddress], () => {
  calculateProfit()
})
</script>

<style scoped>
/* Component-specific styles can be added here if needed */
</style> 